import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { FarmProvider, useFarm } from "@/context/FarmContext";
import Navbar from "@/components/Navbar";
import { Toaster } from "@/components/ui/sonner";
import Dashboard from "@/pages/Dashboard";
import Activities from "@/pages/Activities";
import Financials from "@/pages/Financials";
import BalanceSheet from "@/pages/BalanceSheet";
import Expenses from "@/pages/Expenses";
import Insights from "@/pages/Insights";
import Yields from "@/pages/Yields";
import Crops from "@/pages/Crops";
import Monthly from "@/pages/Monthly";
import Settings from "@/pages/Settings";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthPage from "@/pages/Auth";
import { Sprout } from "lucide-react";

function Shell() {
  const { ready, err } = useFarm();
  if (err) {
    return <div className="min-h-screen grid place-items-center p-8"><pre data-testid="load-error" className="text-destructive text-sm whitespace-pre-wrap">LOAD ERROR: {err}</pre></div>;
  }
  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="flex flex-col items-center gap-3 animate-pulse">
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary text-primary-foreground">
            <Sprout className="h-6 w-6" />
          </div>
          <p className="text-sm text-muted-foreground font-mono-nums">Loading farm ledger…</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="mx-auto max-w-[1400px] px-4 sm:px-6 py-6 sm:py-8">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/activities" element={<Activities />} />
          <Route path="/financials" element={<Financials />} />
          <Route path="/balance-sheet" element={<BalanceSheet />} />
          <Route path="/expenses" element={<Expenses />} />
          <Route path="/yields" element={<Yields />} />
          <Route path="/crops" element={<Crops />} />
          <Route path="/monthly" element={<Monthly />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </main>
    </div>
  );
}

function FullLoader({ label }) {
  return (
    <div className="min-h-screen grid place-items-center">
      <div className="flex flex-col items-center gap-3 animate-pulse">
        <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary text-primary-foreground">
          <Sprout className="h-6 w-6" />
        </div>
        <p className="text-sm text-muted-foreground font-mono-nums">{label}</p>
      </div>
    </div>
  );
}

function Gate() {
  const { user } = useAuth();
  if (user === null) return <FullLoader label="Loading…" />;
  if (!user) return <AuthPage />;
  return (
    <FarmProvider>
      <Shell />
    </FarmProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Gate />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
